import { Artefact, Document } from "@bitory-gmbh/bsm";
import { Process } from "@/services/backend/processes/types/process";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { Position } from "@/services/backend/htz/position/types/position";
import {
  Address,
  newAddress,
} from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import { Representative } from "@/services/backend/representatives/types/representative";
import { Common } from "@/services/backend/documents/types/common";

export interface Order extends Document, Artefact, Common {
  offerId: string | null;
  date: string | null;
  positions: Position[];
  channel: Channel | null;
  customerReference: string;
  internalComment: string;
  postalAddress: Address;
  serviceAddress: Address;
  contact: Contact;
  representative: Representative;
}

export enum Channel {
  EMAIL = "email",
  PHONE = "phone",
  POST = "post",
  FAX = "fax",
}

export function newOrder(
  id: string,
  process: Process,
  branchId: string,
  serviceObjectId: string,
): Order {
  return {
    id,
    documentKind: GSMDocumentKind.Order,
    processId: process.id,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    companyId: process.companyId,
    branchId,
    customerId: process.customerId!,
    serviceObjectId,
    date: new Date().toISOString(),
    offerId: null,
    positions: [],
    channel: null,
    customerReference: "",
    internalComment: "",
    postalAddress: newAddress(),
    serviceAddress: newAddress(),
    contact: {
      salutation: "",
      title: "",
      name: "",
      function: "",
      email: "",
      phone: "",
      mobilephone: "",
    } as Contact,
    representative: {
      name: "",
      email: "",
      phone: "",
      mobilephone: "",
    } as Representative,
  };
}

export function updateDate(order: Order, date: string | null): Order {
  return {
    ...order,
    date,
  };
}

export function updateCustomerReference(
  order: Order,
  customerReference: string,
): Order {
  return {
    ...order,
    customerReference,
  };
}

export function updateInternalComment(
  order: Order,
  internalComment: string,
): Order {
  return {
    ...order,
    internalComment,
  };
}

export function addPosition(order: Order, position: Position): Order {
  if (order.positions.some((pos) => pos.id === position.id)) {
    return order;
  }

  return {
    ...order,
    positions: [
      ...order.positions,
      {
        ...position,
        number: (order.positions.length + 1).toString(),
      },
    ],
  };
}

export function updatePosition(order: Order, position: Position): Order {
  return {
    ...order,
    positions: order.positions.map((pos) => {
      if (pos.id === position.id) {
        return position;
      }
      return pos;
    }),
  };
}

export function deletePosition(order: Order, positionId: string): Order {
  return {
    ...order,
    positions: order.positions
      .filter((pos) => pos.id !== positionId)
      .map((pos, idx) => ({
        ...pos,
        number: (idx + 1).toString(),
      })),
  };
}

export function updatePostalAddress(order: Order, address: Address): Order {
  return {
    ...order,
    postalAddress: address,
  };
}

export function updateServiceAddress(order: Order, address: Address): Order {
  return {
    ...order,
    serviceAddress: address,
  };
}

export function updateRepresentative(
  order: Order,
  representative: Representative,
): Order {
  return {
    ...order,
    representative,
  };
}
